import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ConfirmDialogUI, TabsUI } from '../../Interface';
import EmployeeAbsenceRequestList from './EmployeeAbsenceRequestList';
import EmployeeAbsenceRequestForm from './EmployeeAbsenceRequestForm';
import EmployeeAbsenceRequestDetails from './EmployeeAbsenceRequestDetails';
import {
  EmployeeAbsenceApprovalStatus,
  EmployeeAbsenceApprovalStatusOption,
} from '../../../config/employeeAbsenceRequest';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems } from '../../../utils/helper';
import useUsers from '../../../context/UsersContext';
import dayjs from 'dayjs';
import employeeAbsenceService from '../../../service/employeeAbsence.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import colorFinder from '../../../utils/colorFinder';

export const EmployeeAbsenceRequestPage = () => {
  const { user, getUser } = useUsers();
  const [activeTab, setActiveTab] = useState(0);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    user: [],
    employee: [],
  });
  const [autocompleteData, setAutocompleteData] = useState({});

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };

  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    initDefaultValues();
  }, []);

  useEffect(() => {
    getRequests();
  }, [user?.userId]);

  const getRequests = () => {
    if (!user?.userId) return;

    const query = {
      filter: {
        filterModel: {
          items: [
            {
              field: 'createdBy',
              operator: 'is',
              value: user.userId,
            },
            {
              field: 'mainCategory',
              operator: 'is',
              value: 1,
            },
          ],
          logicOperator: 'and',
        },
      },
      sorting: {
        sortModel: [{ field: 'createdAt', sort: 'desc' }],
      },
    };

    employeeAbsenceService.getAbsences(query).then((data) => {
      setRequests(data.rows);
    });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          employee: resp[1],
        };
        setAutocompleteData(aData);

        const filteredEmployee = aData.employee.filter((employee) => employee.active);

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          employee: generateAutocompleteItems(filteredEmployee, 'name', 'id'),
        };

        setAutocompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  const handleCreate = (values) => {
    const insertObj = {
      ...values,
      startDate: dayjs(values.startDate).startOf('day').add(12, 'hours').toISOString(),
      endDate: dayjs(values.endDate).startOf('day').add(12, 'hours').toISOString(),
      mainCategory: 1,
      subCategory: null,
      approvalStatus: EmployeeAbsenceApprovalStatus.PENDING,
    };

    employeeAbsenceService.createAbsence(insertObj).then((data) => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });

      getRequests();
      setActiveTab(0);
    });
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogData(defConfirmDialogData);
  };

  const handleWithdraw = (id) => {
    setConfirmDialogData({
      open: true,
      message: 'Biztosan vissza szeretné vonni az igénylést?',
      handleChangeElement: () => withdrawRequest(id),
    });
  };

  const withdrawRequest = (id) => {
    employeeAbsenceService.withdrawAbsence(id).then(() => {
      addNotification({
        content: 'Az igénylés sikeresen visszavonásra került!',
        type: NotificationType.SUCCESS,
      });

      onCloseConfirmDialog();
      setSelectedRequest(null);
      getRequests();
    });
  };

  const getStatusData = (statusVal) => {
    const status = EmployeeAbsenceApprovalStatusOption.find((item) => item.value === statusVal);
    const statusColor = colorFinder(status.color);

    return { label: status.label, ...statusColor };
  };

  const tabs = [
    {
      id: 0,
      name: 'Saját igénylések',
      content: (
        <EmployeeAbsenceRequestList
          requests={requests}
          onSelect={setSelectedRequest}
          onCreate={() => setActiveTab(1)}
          getStatusData={getStatusData}
        />
      ),
    },
    {
      id: 1,
      name: 'Új igénylés',
      content: (
        <EmployeeAbsenceRequestForm
          onSubmit={handleCreate}
          autocompleteOptions={autocompleteOptions}
          autocompleteData={autocompleteData}
        />
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          mt: { xs: 0, md: 2 },
          maxWidth: 600,
          mx: 'auto',
          minHeight: { xs: '100dvh', md: 'calc(100vh - 95px)' },
          bgcolor: '#F5F8FB',
          borderRadius: 2,
        }}
      >
        {selectedRequest ? (
          <EmployeeAbsenceRequestDetails
            request={requests.find((item) => item.id === selectedRequest.id)}
            onWithdraw={handleWithdraw}
            onBack={() => setSelectedRequest(null)}
            getStatusData={getStatusData}
          />
        ) : (
          <TabsUI key={activeTab} activeTab={activeTab} handleTabClick={handleTabClick}>
            {tabs.map((tab) => (
              <Box key={tab.id} name={tab.name}>
                {tab.content}
              </Box>
            ))}
          </TabsUI>
        )}
      </Box>
      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={onCloseConfirmDialog}
        message={confirmDialogData.message}
      />
    </>
  );
};

export default EmployeeAbsenceRequestPage;
